import { useEffect, useRef, useState } from 'react';

interface SignupHeroProps {
  activeTab: 'signup' | 'login';
  onTabChange: (tab: 'signup' | 'login') => void;
}

interface AnimatedStatProps {
  target: number;
  prefix?: string;
  suffix?: string;
  label: string;
  trigger: boolean;
}

const AnimatedStat = ({ target, prefix = '', suffix = '', label, trigger }: AnimatedStatProps) => {
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!trigger) return;
    let start = 0;
    const duration = 1300;
    const startTime = performance.now();

    const step = (currentTime: number) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);
      // ease-out cubic
      const eased = 1 - Math.pow(1 - progress, 3);
      const current = Math.floor(eased * target);
      setCount(current);
      if (progress < 1) requestAnimationFrame(step);
    };

    requestAnimationFrame(step);
  }, [trigger, target]);

  return (
    <div>
      <p className="text-white font-bold text-2xl">
        {prefix}{count}{suffix}
      </p>
      <p className="text-ea-gray text-xs mt-0.5">{label}</p>
    </div>
  );
};

const SignupHero = ({ activeTab, onTabChange }: SignupHeroProps) => {
  const statsRef = useRef<HTMLDivElement>(null);
  const [statsVisible, setStatsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setStatsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.3 }
    );
    if (statsRef.current) observer.observe(statsRef.current);
    return () => observer.disconnect();
  }, []);

  const scrollToAuth = (tab: 'signup' | 'login') => {
    onTabChange(tab);
    setTimeout(() => {
      const el = document.getElementById('auth-section');
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 50);
  };

  return (
    <section className="relative pt-32 pb-20 overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0">
        <img
          src="https://public.readdy.ai/ai/img_res/d2bb54ec7ad492caabde9efd9fc8ae5e.jpg"
          alt=""
          className="w-full h-full object-cover object-top opacity-10"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-ea-black/80 via-ea-black/90 to-ea-black"></div>
        <div className="absolute inset-0 bg-gradient-to-r from-ea-blue/5 via-transparent to-ea-blue/5"></div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          {/* Left - Copy */}
          <div>
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-ea-blue/10 border border-ea-blue/30 mb-6">
              <span className="w-1.5 h-1.5 rounded-full bg-ea-blue animate-pulse"></span>
              <span className="text-ea-blue text-xs font-medium uppercase tracking-wider">Member Portal</span>
            </div>
            <h1 className="text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
              Stay Ahead with the<br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-ea-blue to-ea-blue-electric">
                Latest AI Automations
              </span>
            </h1>
            <p className="text-ea-gray text-lg leading-relaxed mb-8 max-w-xl">
              Create your account and join our member newsletter to get instant updates on new automation services, exclusive offers, business tools, and Everything Automation news, straight to your inbox.
            </p>

            {/* Stats strip with count-up */}
            <div ref={statsRef} className="flex flex-wrap items-center gap-8 mb-8">
              <AnimatedStat target={800} suffix="+" label="Members" trigger={statsVisible} />
              <div>
                <p className="text-white font-bold text-2xl">&lt; 2hrs</p>
                <p className="text-ea-gray text-xs mt-0.5">Avg. Response</p>
              </div>
              <AnimatedStat target={500} suffix="+" label="UK Businesses" trigger={statsVisible} />
            </div>

            {/* Trust badges */}
            <div className="flex flex-wrap items-center gap-3">
              {[
                { icon: 'ri-shield-check-line', text: 'GDPR Compliant' },
                { icon: 'ri-lock-line', text: 'Secure &amp; Private' },
                { icon: 'ri-spam-2-line', text: 'No Spam, Ever' },
              ].map((badge) => (
                <div
                  key={badge.text}
                  className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-ea-dark-card border border-ea-dark-border"
                >
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className={`${badge.icon} text-ea-blue text-xs`}></i>
                  </div>
                  <span className="text-ea-gray text-xs" dangerouslySetInnerHTML={{ __html: badge.text }} />
                </div>
              ))}
            </div>
          </div>

          {/* Right - Tab toggle CTA */}
          <div className="flex flex-col items-center">
            <div className="w-full max-w-md bg-ea-dark-card border border-ea-dark-border rounded-2xl p-2">
              <div className="flex">
                <button
                  onClick={() => scrollToAuth('signup')}
                  className={`flex-1 py-3 text-sm font-semibold rounded-xl transition-all duration-300 cursor-pointer whitespace-nowrap ${
                    activeTab === 'signup'
                      ? 'bg-ea-blue text-white'
                      : 'text-ea-gray hover:text-white'
                  }`}
                >
                  Create Account
                </button>
                <button
                  onClick={() => scrollToAuth('login')}
                  className={`flex-1 py-3 text-sm font-semibold rounded-xl transition-all duration-300 cursor-pointer whitespace-nowrap ${
                    activeTab === 'login'
                      ? 'bg-ea-blue text-white'
                      : 'text-ea-gray hover:text-white'
                  }`}
                >
                  Sign In
                </button>
              </div>
            </div>

            <p className="text-ea-gray/60 text-xs mt-4 text-center">
              Join 800+ business owners already using our platform
            </p>

            {/* Trust badge */}
            <div className="mt-4 flex items-center gap-2 px-4 py-2 rounded-full bg-ea-blue/8 border border-ea-blue/20">
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-shield-check-fill text-ea-blue/70 text-sm"></i>
              </div>
              <span className="text-ea-gray/60 text-xs font-medium tracking-wide">Trusted by UK businesses</span>
            </div>
          </div>
        </div>

        {/* Scroll arrow */}
        <div className="flex justify-center mt-14">
          <button
            onClick={() => scrollToAuth(activeTab)}
            className="flex flex-col items-center gap-2 cursor-pointer group bg-transparent border-none"
          >
            <span className="text-ea-gray text-xs uppercase tracking-widest">Get Started</span>
            <div className="w-8 h-8 flex items-center justify-center rounded-full border border-ea-dark-border group-hover:border-ea-blue transition-colors duration-300">
              <i className="ri-arrow-down-line text-ea-gray group-hover:text-ea-blue text-sm transition-colors duration-300"></i>
            </div>
          </button>
        </div>
      </div>
    </section>
  );
};

export default SignupHero;
